/**
 * @layer/canonical-data/member-profile-gaps — the single source of truth for
 * "does this member's profile still have an essential gap worth collecting
 * after a session?"
 *
 * Consumed by:
 *   - `computeMemberProfileGaps` — the `trainer_postsession_capture` (T3) saga
 *     ONLY, to decide whether to prompt the trainer at all (a COMPLETE profile
 *     ⇒ no prompt) and to NAME the missing fields in the prompt body. This is
 *     the gate's single production call site;
 *   - `MEMBER_PROFILE_FIELD_LABELS` — the chat translator's staff prompt, which
 *     DERIVES its profile-capture routing table from the label map
 *     (`buildProfileFieldRouting`) so the prompt and the gate cannot drift;
 *   - `cleanProfileFieldValue` / `MEMBER_GENDER_OPTIONS` — the Morning Brief,
 *     the `/trainers/:id/today` block and the dashboard member page.
 *
 * ONE label map so the saga's send-decision and the agent's routing table can
 * never drift, over TWO field sets (required vs capturable — see below). The
 * sets are exported constants; they are the ONLY place to edit the gate.
 *
 * Field storage (see migration 001 + the consultation-form OCR):
 *   - `display_name` / `email` / `phone` are first-class `members` columns.
 *   - `gender` lives at `members.metadata.gender`, and `source` — how the
 *     member heard about the studio ("Word of mouth", "Google", …), the
 *     `Source` box on the paper consultation form — at
 *     `members.metadata.source`. `source` is read through
 *     `cleanProfileFieldValue` (unlike the other top-level facets, which are
 *     read raw) so a `skipped` sentinel written by an agent or an import can
 *     never falsely close the gap.
 *   - `postcode` (the only address datum stored) lives at
 *     `members.metadata.postcode` — CAPTURABLE but NOT required, see below.
 *   - EMS kit sizes live at `members.metadata.{vest,trouser}` (both required).
 *   - the consultation goal + primary focus area live at
 *     `members.metadata.consultation.{CF_3_Goal,CF_5_FocusArea}` — with the
 *     member's OWN webchat onboarding answers
 *     (`members.metadata.preferences["Onboarding - Goals"/"Onboarding - Activities"]`)
 *     accepted as fallbacks: a natively-onboarded member who already answered
 *     is NOT a gap, so the T3 post-session prompt no longer re-asks the
 *     trainer for a goal the member typed themselves. The Goals pairing is the
 *     one the Trainer Morning Brief also merges; the Activities→focus pairing
 *     is THIS module's own collection decision (the brief has shown Activities
 *     under its own label since 2026-09-03 — see `ONBOARDING_FALLBACK_KEYS`).
 *
 * REQUIRED vs CAPTURABLE — two sets, deliberately (2026-09-03). A field can be
 * worth STORING without being worth PROMPTING for, and collapsing the two is
 * what kept trainers re-nagged forever for a datum nobody ever supplies:
 *   - `ESSENTIAL_MEMBER_PROFILE_FIELDS` is the GATE — absence ⇒ a gap ⇒ the T3
 *     post-session prompt fires and names the field.
 *   - `OPTIONAL_MEMBER_PROFILE_FIELDS` is stored + displayed + routable by the
 *     chat agent when a trainer volunteers it, but is NEVER a gap.
 *   - `CAPTURABLE_MEMBER_PROFILE_FIELDS` (their concatenation) keys the label
 *     map, so the chat agent's profile-capture routing table still knows where
 *     an optional field goes. Dropping a field from the label map instead of
 *     moving it here is the bug to avoid: the agent would stop knowing where to
 *     PUT a postcode a member volunteers.
 *
 * `postcode` is OPTIONAL as of 2026-09-03 (Mario's call). It was filled for 22
 * of 2856 live members (0.8%) while being one of nine gate fields, so it was
 * the sole remaining gap for 9 of the 56 members with a past session — those
 * trainers were re-prompted for a postcode after every single session forever.
 * It is still captured by the OCR and still writable; it just no longer gates.
 *
 * Deliberately EXCLUDED entirely (not even capturable here): injuries
 * (`CF_8_Injuries*`, "injuries, if any") and the 2nd/3rd focus areas
 * (`CF_5_FocusArea_1/_2`).
 */

/**
 * The minimal member shape this module reads. Plain (unbranded) so the helper
 * stays dependency-light and callable from both the saga and the repository.
 */
export interface MemberProfileForGaps {
  readonly display_name: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  readonly metadata: Record<string, unknown> | null;
}

/**
 * The essential/consultation fields whose absence makes a member "incomplete"
 * (and so worth a post-session collection prompt). SINGLE source of truth —
 * edit here to change the gate. Order drives the order fields are named in the
 * prompt.
 */
export const ESSENTIAL_MEMBER_PROFILE_FIELDS = [
  "display_name",
  "email",
  "phone",
  "source",
  "gender",
  "vest",
  "trouser",
  "consultation.CF_3_Goal",
  "consultation.CF_5_FocusArea",
] as const;

export type EssentialMemberProfileField =
  (typeof ESSENTIAL_MEMBER_PROFILE_FIELDS)[number];

/**
 * Fields the capture path STORES and the agent can route, but whose absence is
 * NOT a gap — they never make a profile incomplete and never appear in a
 * post-session prompt. See "REQUIRED vs CAPTURABLE" in the module doc.
 */
export const OPTIONAL_MEMBER_PROFILE_FIELDS = ["postcode"] as const;

export type OptionalMemberProfileField =
  (typeof OPTIONAL_MEMBER_PROFILE_FIELDS)[number];

/**
 * Every field the profile-capture path understands = required + optional. Keys
 * the label map, so the chat agent's routing table covers optional fields too.
 */
export const CAPTURABLE_MEMBER_PROFILE_FIELDS = [
  ...ESSENTIAL_MEMBER_PROFILE_FIELDS,
  ...OPTIONAL_MEMBER_PROFILE_FIELDS,
] as const;

export type MemberProfileField =
  (typeof CAPTURABLE_MEMBER_PROFILE_FIELDS)[number];

/**
 * Human labels for naming a field in the trainer prompt / the UI. Keyed on the
 * CAPTURABLE set (not the essential one) so an optional field keeps a label —
 * that label is what the chat agent's profile-capture routing table is derived
 * from, and a field with no label is a field the agent cannot place.
 */
export const MEMBER_PROFILE_FIELD_LABELS: Record<MemberProfileField, string> = {
  display_name: "name",
  email: "email",
  phone: "phone number",
  source: "referral source",
  gender: "gender",
  vest: "vest size",
  trouser: "trouser size",
  "consultation.CF_3_Goal": "training goal",
  "consultation.CF_5_FocusArea": "focus areas",
  postcode: "postcode",
};

// ---------------------------------------------------------------------------
// Member gender — the member's OWN gender at `members.metadata.gender`. SINGLE
// canonical value set + normalizer so every writer (staff dashboard dropdown,
// the WhatsApp chat agent's `update_member`, imports) and every reader (the
// dashboard "Gender" dropdown, this gap checker) share ONE representation. A
// free-form value like "female" / "F" / "woman" would otherwise not match the
// dropdown's canonical options and render as "— not set —" even though the
// value is present. Distinct from the onboarding "Trainer gender" PREFERENCE
// (`metadata.preferences`) and the AI-enrichment inferred gender
// (`metadata.ai_enrichment.inferred_gender`).
// ---------------------------------------------------------------------------

/** The canonical member-gender values (also the dashboard dropdown options). */
export const MEMBER_GENDER_OPTIONS = [
  "Female",
  "Male",
  "Other",
  "Prefer not to say",
] as const;

export type MemberGender = (typeof MEMBER_GENDER_OPTIONS)[number];

/** Free-form synonyms → canonical value (keys are trimmed + lower-cased). */
const MEMBER_GENDER_SYNONYMS: Readonly<Record<string, MemberGender>> = {
  female: "Female",
  f: "Female",
  woman: "Female",
  women: "Female",
  girl: "Female",
  lady: "Female",
  male: "Male",
  m: "Male",
  man: "Male",
  men: "Male",
  boy: "Male",
  other: "Other",
  "non-binary": "Other",
  "non binary": "Other",
  nonbinary: "Other",
  nb: "Other",
  enby: "Other",
  "prefer not to say": "Prefer not to say",
  prefer_not_to_say: "Prefer not to say",
  "n/a": "Prefer not to say",
  undisclosed: "Prefer not to say",
  declined: "Prefer not to say",
};

/**
 * Canonicalize a raw gender value to one of `MEMBER_GENDER_OPTIONS`, or `null`
 * when the input is blank/unrecognised (callers keep the raw value in that
 * case rather than lose it). Case-insensitive; matches the canonical labels
 * directly and a small synonym table ("female" → "Female", "F" → "Female", …).
 */
export function normalizeMemberGender(raw: unknown): MemberGender | null {
  if (typeof raw !== "string") return null;
  const key = raw.trim().toLowerCase().replace(/\s+/g, " ");
  if (key.length === 0) return null;
  for (const option of MEMBER_GENDER_OPTIONS) {
    if (option.toLowerCase() === key) return option;
  }
  return MEMBER_GENDER_SYNONYMS[key] ?? null;
}

export interface MemberProfileGaps {
  /** The essential fields that are absent/blank, in `ESSENTIAL_*` order. */
  readonly missing: EssentialMemberProfileField[];
  /** `missing` mapped through `MEMBER_PROFILE_FIELD_LABELS` (for prompts/UI). */
  readonly missingLabels: string[];
  /** True when nothing is missing — the saga skips the prompt in that case. */
  readonly isComplete: boolean;
}

/** A value counts as PRESENT unless it's null/undefined or a blank string. */
function isPresent(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  return true;
}

/**
 * Normalise a consultation CF_* / onboarding-preference value to a readable
 * string, or `undefined` when it holds no real answer. SINGLE implementation —
 * shared by this module's gap check, the Trainer Morning Brief's profile
 * mining and the `/trainers/:id/today` onboarding block, so "what counts as
 * an answer" can never drift between the prompt and the displays.
 *
 * The legacy import stored many CF_* values as JSON-stringified arrays (e.g.
 * `["Weight Loss","Muscle Growth"]`) and the webchat onboarding FE does the
 * same for multi-selects (`"[\"Boxing\",\"HIIT\"]"`); flatten those to a
 * readable comma list. The FE additionally stores the literal sentinel
 * `skipped` for an unanswered question — as a bare string, as the
 * DOUBLE-JSON-ENCODED scalar `"\"skipped\""` (a quoted string inside a
 * string), and, for a multi-select the member skipped with zero picks, as the
 * one-element array `["skipped"]` (`OnboardingQuestions.handleNext` in the
 * Hercie panel) — all of which mean "no answer" and must never count as one
 * or surface on a trainer's brief as if the member said the word "skipped".
 * Plain strings pass through trimmed; blank / sentinel / non-string ⇒
 * undefined.
 */
export function cleanProfileFieldValue(v: unknown): string | undefined {
  if (typeof v !== "string") return undefined;
  const s = v.trim();
  if (s.length === 0) return undefined;
  // Double-JSON-encoded scalar (`"\"skipped\""`, `"\"Female\""`): unwrap and
  // re-clean, so the sentinel and blank rules below apply to the inner value.
  if (s.startsWith('"') && s.endsWith('"') && s.length >= 2) {
    try {
      const parsed: unknown = JSON.parse(s);
      if (typeof parsed === "string") return cleanProfileFieldValue(parsed);
    } catch {
      // Not valid JSON — fall through and treat the quoted text as literal.
    }
  }
  if (isSkippedSentinel(s)) return undefined;
  if (s.startsWith("[") && s.endsWith("]")) {
    try {
      const parsed: unknown = JSON.parse(s);
      if (Array.isArray(parsed)) {
        // The sentinel inside an array is still the sentinel (`["skipped"]`),
        // and a real pick beside it is still a pick.
        const items = parsed
          .filter(
            (x): x is string =>
              typeof x === "string" &&
              x.trim().length > 0 &&
              !isSkippedSentinel(x.trim()),
          )
          .map((x) => x.trim());
        return items.length > 0 ? items.join(", ") : undefined;
      }
    } catch {
      // Not valid JSON — fall through and return the trimmed string as-is.
    }
  }
  return s;
}

/** The FE's "unanswered" sentinel, in any letter case. */
function isSkippedSentinel(s: string): boolean {
  return /^skipped$/i.test(s);
}

/**
 * The onboarding-answer fallback per consultation field — THIS gap check's
 * own decision about what counts as "collected". "Goals" is a training goal
 * verbatim, and the brief merges it the same way. "Activities" is NOT a
 * focus area — since 2026-09-03 the Morning Brief renders it under its own
 * `Activities:` label and fills `Focus:` from the consultation only — but it
 * is kept here as the focus-area fallback deliberately: it is the closest
 * planning signal the webchat collects (the form has no body-area question),
 * and dropping it would make the T3 post-session prompt start re-asking every
 * natively-onboarded member's trainer for a focus area. Change this pairing
 * only as its own decision, never as a side effect of a brief edit.
 */
const ONBOARDING_FALLBACK_KEYS: Readonly<
  Record<"CF_3_Goal" | "CF_5_FocusArea", string>
> = {
  CF_3_Goal: "Onboarding - Goals",
  CF_5_FocusArea: "Onboarding - Activities",
};

/** `metadata.preferences` as an object (`{}` when absent/malformed). */
function preferencesOf(
  meta: Record<string, unknown>,
): Record<string, unknown> {
  const raw = meta["preferences"];
  return raw !== null && typeof raw === "object" && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {};
}

/** Resolve one capturable field's raw value from the member shape. */
function readField(
  member: MemberProfileForGaps,
  field: MemberProfileField,
): unknown {
  const meta = member.metadata ?? {};
  switch (field) {
    case "display_name":
      return member.display_name;
    case "email":
      return member.email;
    case "phone":
      return member.phone;
    case "postcode":
      return meta["postcode"];
    // Cleaned, not raw: `source` is agent- and import-written, so a `skipped`
    // sentinel must not count as an answer the way a raw read would.
    case "source":
      return cleanProfileFieldValue(meta["source"]);
    case "gender":
      return meta["gender"];
    case "vest":
      return meta["vest"];
    case "trouser":
      return meta["trouser"];
    case "consultation.CF_3_Goal":
    case "consultation.CF_5_FocusArea": {
      const raw = meta["consultation"];
      const consultation =
        raw !== null && typeof raw === "object"
          ? (raw as Record<string, unknown>)
          : {};
      const cfKey = field.slice(
        "consultation.".length,
      ) as keyof typeof ONBOARDING_FALLBACK_KEYS;
      // Cleaned, not raw: a `skipped` sentinel is NOT an answer on either
      // side, and the member's own onboarding answer fills a CF_* gap.
      return (
        cleanProfileFieldValue(consultation[cfKey]) ??
        cleanProfileFieldValue(
          preferencesOf(meta)[ONBOARDING_FALLBACK_KEYS[cfKey]],
        )
      );
    }
  }
}

/** Compute the member's essential-profile gaps (pure). */
export function computeMemberProfileGaps(
  member: MemberProfileForGaps,
): MemberProfileGaps {
  const missing: EssentialMemberProfileField[] = [];
  for (const field of ESSENTIAL_MEMBER_PROFILE_FIELDS) {
    if (!isPresent(readField(member, field))) missing.push(field);
  }
  return {
    missing,
    missingLabels: missing.map((f) => MEMBER_PROFILE_FIELD_LABELS[f]),
    isComplete: missing.length === 0,
  };
}

/** "a" · "a and b" · "a, b and c" — for naming missing fields in the prompt. */
export function joinWithAnd(items: readonly string[]): string {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0] as string;
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1] as string}`;
}
